import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { RELATIONSHIP_ANALYSIS } from '../../data/relationshipAnalysis';

interface AnalysisViewProps {
  onBackToChat: () => void;
}

interface AiMessage {
  id: string;
  sender: 'user' | 'ai';
  text: string;
  time: string;
}

const SUGGESTED_PROMPTS = [
  'How should I approach our in-person meeting in ~80 hours without being defensive?',
  'Analyze the psychological shift between our Durga Puja intimacy and the Kolkata incident.',
  'What are the fearful-avoidant attachment cues in Ankita’s recent messages?',
  'Give me an immediate 5-step emotional grounding routine for tonight.',
];

export const AnalysisView: React.FC<AnalysisViewProps> = ({ onBackToChat }) => {
  const [activeTab, setActiveTab] = useState<'ai' | 'timeline' | 'psychology' | 'roadmap' | 'all'>('ai');
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiMessages, setAiMessages] = useState<AiMessage[]>([]);

  const report = RELATIONSHIP_ANALYSIS;

  const handleAskAi = async (questionText: string) => {
    const q = questionText.trim();
    if (!q || aiLoading) return;

    const userMsg: AiMessage = {
      id: `u_${Date.now()}`,
      sender: 'user',
      text: q,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setAiMessages(prev => [...prev, userMsg]);
    setAiQuestion('');
    setAiLoading(true);
    setAiError(null);

    try {
      const res = await fetch('/api/ai/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          question: q,
          history: aiMessages.slice(-4).map(m => ({
            role: m.sender === 'user' ? 'user' : 'model',
            text: m.text,
          })),
        }),
      });

      const data = (await res.json()) as {
        success?: boolean;
        answer?: string;
        error?: string;
        message?: string;
      };

      if (!res.ok || !data.success) {
        if (data.error === 'NO_API_KEY') {
          setAiError(
            'Gemini API key is not configured yet. Go to Admin Panel → Settings → Gemini AI to add your key and enable dynamic live queries.'
          );
        } else {
          setAiError(data.message || data.error || 'Failed to generate AI analysis.');
        }
        return;
      }

      const aiMsg: AiMessage = {
        id: `ai_${Date.now()}`,
        sender: 'ai',
        text: data.answer || 'Analysis complete.',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };

      setAiMessages(prev => [...prev, aiMsg]);
    } catch (err: unknown) {
      setAiError(err instanceof Error ? err.message : 'Network error communicating with AI endpoint.');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="analysis-container" role="region" aria-label="Dynamic Relationship Analysis">
      {/* Human Editorial Header */}
      <div className="dossier-header-clean">
        <div className="dossier-header-top">
          <div className="dossier-brand-clean">
            <span className="clean-tag">Living Relationship Dossier</span>
            <span className="clean-status-dot" />
            <span className="clean-status-label">Active Reflection • Meeting in ~80 Hours</span>
          </div>
          <div className="dossier-actions-clean">
            <Link to="/" className="clean-nav-link" title="Return to home">
              ← Keepsake
            </Link>
            <button type="button" className="clean-nav-link" onClick={onBackToChat}>
              💬 Chat Stream
            </button>
          </div>
        </div>

        <h1 className="dossier-headline">Ratnadwip &amp; Ankita</h1>
        <p className="dossier-lead">
          An evolving, non-judgmental space for emotional clarity, psychological grounding, and longitudinal analysis across 33,000+ interactions.
        </p>

        {/* Dynamic Key Metrics - Quiet & Human */}
        <div className="human-metrics-strip">
          <div className="human-metric">
            <span className="hm-label">Recorded Dialogue</span>
            <span className="hm-val">33,186+</span>
            <span className="hm-sub">Instagram &amp; WhatsApp archive</span>
          </div>
          <div className="human-metric">
            <span className="hm-label">Timeline Arc</span>
            <span className="hm-val">June – Oct 2026</span>
            <span className="hm-sub">4.5 months deepening connection</span>
          </div>
          <div className="human-metric">
            <span className="hm-label">Current State</span>
            <span className="hm-val" style={{ color: 'var(--red-7)' }}>
              Acute Boundary
            </span>
            <span className="hm-sub">Space held ahead of confirmed meeting</span>
          </div>
          <div className="human-metric">
            <span className="hm-label">Next Inflection</span>
            <span className="hm-val">In-Person Dialogue</span>
            <span className="hm-sub">~80 hours to clarify and reconnect</span>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="clean-tabs-bar" role="tablist">
          <button
            type="button"
            className={`clean-tab-btn ${activeTab === 'ai' ? 'active' : ''}`}
            onClick={() => setActiveTab('ai')}
          >
            ✦ Ask Gemini AI
          </button>
          <button
            type="button"
            className={`clean-tab-btn ${activeTab === 'timeline' ? 'active' : ''}`}
            onClick={() => setActiveTab('timeline')}
          >
            Timeline &amp; Milestones
          </button>
          <button
            type="button"
            className={`clean-tab-btn ${activeTab === 'psychology' ? 'active' : ''}`}
            onClick={() => setActiveTab('psychology')}
          >
            Psychological Diagnosis
          </button>
          <button
            type="button"
            className={`clean-tab-btn ${activeTab === 'roadmap' ? 'active' : ''}`}
            onClick={() => setActiveTab('roadmap')}
          >
            Meeting Roadmap
          </button>
          <button
            type="button"
            className={`clean-tab-btn ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            Complete Journal
          </button>
        </div>
      </div>

      {/* TAB 1: LIVE GEMINI AI INTERACTIVE SYSTEM */}
      {(activeTab === 'ai' || activeTab === 'all') && (
        <section className="clean-card ai-workspace-card">
          <div className="clean-card-header">
            <div>
              <span className="clean-card-tag">Living Intelligence</span>
              <h2 className="clean-card-title">Ask Gemini AI Anything</h2>
            </div>
            <span className="ai-model-badge">Powered by Google Gemini</span>
          </div>

          <p className="clean-card-desc">
            Ask any question about emotional signals, conflict causes, or how to prepare for your upcoming meeting. Grounded directly in your relationship timeline and messages.
          </p>

          {/* Quick Suggested Reflection Prompts */}
          <div className="prompts-suggestion-strip">
            <span className="prompts-label">Suggested Inquiries:</span>
            <div className="prompts-chips">
              {SUGGESTED_PROMPTS.map((prompt, i) => (
                <button
                  key={i}
                  type="button"
                  className="prompt-chip"
                  onClick={() => handleAskAi(prompt)}
                  disabled={aiLoading}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>

          {/* AI Q&A Dialogue Feed */}
          {aiMessages.length > 0 && (
            <div className="ai-dialogue-stream">
              {aiMessages.map(msg => (
                <div key={msg.id} className={`ai-message-bubble ${msg.sender}`}>
                  <div className="ai-msg-header">
                    <span className="ai-msg-sender">
                      {msg.sender === 'user' ? 'Ratnadwip' : '✦ Gemini Relational Analyst'}
                    </span>
                    <span className="ai-msg-time">{msg.time}</span>
                  </div>
                  <div className="ai-msg-content">{msg.text}</div>
                </div>
              ))}
            </div>
          )}

          {aiLoading && (
            <div className="ai-thinking-state">
              <div className="subtle-spinner" style={{ width: '20px', height: '20px' }} />
              <span>Gemini is analyzing relationship context and synthesizing perspective...</span>
            </div>
          )}

          {aiError && (
            <div className="ai-error-banner">
              <strong>Notice:</strong> {aiError}
            </div>
          )}

          {/* Live Question Input Box */}
          <form
            onSubmit={e => {
              e.preventDefault();
              handleAskAi(aiQuestion);
            }}
            className="ai-query-form"
          >
            <textarea
              className="ai-query-textarea"
              placeholder="Ask anything... (e.g., 'What should I do if she remains quiet during the meeting?')"
              value={aiQuestion}
              onChange={e => setAiQuestion(e.target.value)}
              rows={2}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAskAi(aiQuestion);
                }
              }}
            />
            <button
              type="submit"
              disabled={aiLoading || !aiQuestion.trim()}
              className="ai-ask-submit-btn"
            >
              {aiLoading ? 'Thinking...' : 'Ask AI'}
            </button>
          </form>
        </section>
      )}

      {/* TAB 2: TIMELINE & MILESTONES */}
      {(activeTab === 'timeline' || activeTab === 'all') && (
        <section className="clean-card">
          <div className="clean-card-header">
            <div>
              <span className="clean-card-tag">Milestone Arc</span>
              <h2 className="clean-card-title">Chronological Turning Points</h2>
            </div>
          </div>

          <div className="human-timeline-list">
            {report.sections[0].content.map((point, index) => {
              const [heading, ...rest] = point.split(':');
              return (
                <div key={index} className="human-timeline-node">
                  <div className="htn-dot" />
                  <div className="htn-body">
                    <h3 className="htn-title">{heading}</h3>
                    <p className="htn-text">{rest.join(':')}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* TAB 3: PSYCHOLOGICAL DIAGNOSIS */}
      {(activeTab === 'psychology' || activeTab === 'all') && (
        <section className="clean-card">
          <div className="clean-card-header">
            <div>
              <span className="clean-card-tag">Psychological Patterns</span>
              <h2 className="clean-card-title">Ambivalence, Warnings &amp; Attachment</h2>
            </div>
          </div>

          <div className="journal-paragraphs">
            {report.sections[1].content.map((p, i) => (
              <p key={i} className="journal-para">
                {p}
              </p>
            ))}
          </div>

          <div className="human-insight-callout">
            <h4 className="hic-title">Core Dynamic Synthesis</h4>
            <p className="hic-text">
              Two truths exist simultaneously: Ankita feels genuine attraction and joy when in physical proximity, but experiences acute fear of vulnerability, causing preemptive boundary declarations (&quot;Stay away&quot;, &quot;I&apos;ll ruin your life&quot;). Her retreat is an active defensive mechanism, not an unprovoked indifference.
            </p>
          </div>
        </section>
      )}

      {/* TAB 4: MEETING ROADMAP */}
      {(activeTab === 'roadmap' || activeTab === 'all') && (
        <section className="clean-card">
          <div className="clean-card-header">
            <div>
              <span className="clean-card-tag">Actionable Protocol</span>
              <h2 className="clean-card-title">Roadmap for the Upcoming Encounter (~80 Hours)</h2>
            </div>
          </div>

          <div className="roadmap-phases-grid">
            <div className="phase-card">
              <span className="phase-num">01</span>
              <h4 className="phase-title">Grounded Calm</h4>
              <p className="phase-desc">
                Do not open with frantic apologies or demands for security. Allow her body language to dictate the initial distance.
              </p>
            </div>

            <div className="phase-card">
              <span className="phase-num">02</span>
              <h4 className="phase-title">Radical Listening</h4>
              <p className="phase-desc">
                Listen to understand why she became upset over Kolkata without defending intentions or cross-examining her feelings.
              </p>
            </div>

            <div className="phase-card">
              <span className="phase-num">03</span>
              <h4 className="phase-title">Honest Centering</h4>
              <p className="phase-desc">
                Own your care calmly: &quot;I care deeply, but I honor your space. If you need friendship or time, I will respect that.&quot;
              </p>
            </div>

            <div className="phase-card">
              <span className="phase-num">04</span>
              <h4 className="phase-title">Emotional Sovereignty</h4>
              <p className="phase-desc">
                Never beg for affection. Dignity and calm self-respect are both healthy and genuinely safe for both people.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Quiet Footer */}
      <footer className="analysis-footer-clean">
        <Link to="/" className="clean-nav-link">
          ← Return to Keepsake
        </Link>
        <button type="button" className="clean-nav-link" onClick={onBackToChat}>
          💬 Back to Messages
        </button>
      </footer>
    </div>
  );
};
